import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiFileText, FiSend } from "react-icons/fi";
import Navbar from "../../../../Shared/AssociateNavbar/Navbar";
import { pdf } from "@react-pdf/renderer";
import InvoicePDF from "../../../Admin/Tharun/Pdf2";
import baseURL from "../../../../Apiurs/Apiurls";
import axios from "axios";
import { AuthContext } from "../../../../AuthContext/Authcontext";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);

const ViewQuotationPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userName, userEmail, userMobile, userRole } = useContext(AuthContext);
  const { data, leadInfo: initialLeadInfo, quotationData } = location.state || {};

  // State to hold the latest leadInfo
  const [leadInfo, setLeadInfo] = useState(initialLeadInfo);
  const [loadingLead, setLoadingLead] = useState(true);
  const [errorLead, setErrorLead] = useState(null);

  const { subtotal, totalGST, grandTotal, products } = data || {};
  const id = leadInfo?.id;
  const [collapsed, setCollapsed] = useState(false);
  const [sendingQuotation, setSendingQuotation] = useState(false);
  const [quotationStatus, setQuotationStatus] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("percentage");
  const [quotationNumber, setQuotationNumber] = useState("");
  const [isQuotationGenerated, setIsQuotationGenerated] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const regardsInfo = {
    name: userName || "N/A",
    email: userEmail || "N/A",
    mobile: userMobile || "N/A",
    role: userRole || "N/A",
  };

  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/company/1`);
        console.log("🏢 Company fetched:", response.data);
        setCompany(response.data);
        setLoading(false);
      } catch (err) {
        console.error("❌ Fetch error:", err);
        setError("Company data not found");
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  // Sync leadInfo with location.state changes
  useEffect(() => {
    if (initialLeadInfo) {
      setLeadInfo(initialLeadInfo);
      setLoadingLead(false);
    }
  }, [initialLeadInfo]);

  // Fetch leadInfo from API to ensure fresh data
  useEffect(() => {
    const fetchLeadInfo = async () => {
      if (!id) return;
      setLoadingLead(true);
      try {
        const response = await axios.get(`${baseURL}/api/leads/${id}`);
        console.log("📋 Lead info fetched:", response.data);
        setLeadInfo(response.data);
        setLoadingLead(false);
      } catch (err) {
        console.error("❌ Error fetching lead info:", err);
        setErrorLead("Failed to fetch lead information");
        setLoadingLead(false);
      }
    };
    fetchLeadInfo();
  }, [id]);

  // Fetch preview next number
  useEffect(() => {
    const fetchPreviewNumber = async () => {
      try {
        const res = await fetch(`${baseURL}/api/get-next-quotation-number`);
        const data = await res.json();
        if (res.ok && data.quotationNumber) {
          setQuotationNumber(data.quotationNumber);
        }
      } catch (err) {
        console.error("Error fetching next quotation number:", err);
      }
    };
    fetchPreviewNumber();
  }, []);

  // Fetch discount
  useEffect(() => {
    const fetchDiscount = async () => {
      if (!id) return;
      try {
        const res = await axios.get(`${baseURL}/api/lead/${id}/discount`);
        if (res.data.success) {
          setDiscount(res.data.discount || res.data.Discount || 0);
          setDiscountType(res.data.discountType || "percentage");
        }
      } catch (err) {
        console.error("Error fetching discount:", err);
      }
    };
    fetchDiscount();
  }, [id]);

  // Generate PDF blob
  const generateQuotationPDF = async () => {
    const today = new Date();
    const dateString = today.toISOString().split("T")[0];

    try {
      console.log("📄 Generating PDF with parameters:", {
        quotationNumber,
        quotationDate: dateString,
        subtotal,
        totalGST,
        grandTotal,
        discount,
        discountType,
        leadInfo,
        quotationData,
        companyInfo: company,
        regardsInfo,
      });

      const blob = await pdf(
        <InvoicePDF
          data={{
            quotationNumber,
            quotationDate: dateString,
            subtotal,
            totalGST,
            grandTotal,
            discount,
            discountType,
          }}
          discount={discount}
          discountType={discountType}
          quotationNumber={quotationNumber}
          leadInfo={leadInfo}
          quotationData={quotationData}
          companyInfo={company}
          regardsInfo={regardsInfo}
        />
      ).toBlob();

      setPdfBlob(blob);
      return blob;
    } catch (err) {
      console.error("❌ Error generating PDF:", err);
      throw err;
    }
  };

  const handleSendQuotation = async () => {
    setSendingQuotation(true);

    try {
      const blob = await generateQuotationPDF();
      const pdfFile = new File([blob], `${quotationNumber}.pdf`, {
        type: "application/pdf",
      });

      console.log("📧 Sending quotation email with parameters:", {
        email: leadInfo?.email,
        quotationNumber,
        leadId: id,
        leadName: leadInfo?.lead_name,
        subtotal,
        totalGST,
        grandTotal,
        products: quotationData,
        message_id: leadInfo?.message_id,
        terms_conditions: leadInfo?.terms_conditions,
        quotation_body: leadInfo?.quotation_body,
        discount,
        discountType,
        pdfFileName: pdfFile.name,
        pdfFileType: pdfFile.type,
        pdfFileSize: pdfFile.size,
        companyInfo: company,
        regardsInfo,
        leadInfo,
      });

      navigate("/email", {
        state: {
          email: leadInfo?.email,
          quotationData: {
            pdfFile,
            quotationNumber,
            leadId: id,
            leadName: leadInfo?.lead_name,
            subtotal,
            totalGST,
            grandTotal,
            products: quotationData,
            message_id: leadInfo?.message_id,
            terms_conditions: leadInfo?.terms_conditions,
            quotation_body: leadInfo?.quotation_body,
            discount,
            discountType,
            companyInfo: company,
            regardsInfo,
            leadInfo,
          },
          autoFillSubject: `Quotation ${quotationNumber} from SBS Company`,
          autoFillBody: `Dear ${leadInfo?.lead_name},\n\nPlease find attached your quotation ${quotationNumber}.\n\nBest regards,\nSBS Company`,
        },
      });
    } catch (err) {
      console.error("❌ Error preparing quotation for email:", err);
      alert("Failed to prepare quotation for email. Please try again.");
    } finally {
      setSendingQuotation(false);
    }
  };

  // Fetch quotation status
  useEffect(() => {
    const fetchQuotationStatus = async () => {
      try {
        const res = await fetch(`${baseURL}/api/quotation-status/${id}`);
        const data = await res.json();
        if (data.sent) {
          setIsQuotationGenerated(true);
        }
      } catch (err) {
        console.error("Error fetching quotation status:", err);
      }
    };
    fetchQuotationStatus();
  }, [id]);

  // Fetch lead quotation status
  useEffect(() => {
    const fetchStatus = async () => {
      if (!id) return;
      try {
        const res = await fetch(`${baseURL}/api/lead-quotation-status/${id}`);
        const data = await res.json();
        if (res.ok) {
          setQuotationStatus(data.quotation_status || "");
        }
      } catch (err) {
        console.error("Error fetching quotation status:", err);
      }
    };
    fetchStatus();
  }, [id]);

  // Handle loading and error states
  if (loadingLead || loading) {
    return <p className="text-center mt-5">Loading...</p>;
  }

  if (errorLead || error || !data || !leadInfo || !quotationData) {
    return (
      <p className="text-center mt-5 text-danger fw-bold">
        {errorLead || error || "No quotation data found"}
      </p>
    );
  }

  return (
    <div className="CollapseContainer">
      <Navbar onToggleSidebar={setCollapsed} />

      <div className={`Collapse ${collapsed ? "collapsed" : ""}`}>
        <div className="container my-4 mx-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button
              className="btn btn-outline-secondary d-flex align-items-center shadow-sm"
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft className="me-2" /> Back
            </button>

            <div className="d-flex gap-2">
              <button
                className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-warning adminviewproducts-viewlead-d-flex adminviewproducts-viewlead-align-items-center"
                onClick={() => {
                  const today = new Date();
                  const dateString = today.toISOString().split("T")[0];

                  navigate("/s-quotation-preview", {
                    state: {
                      quotationNumber,
                      data,
                      leadInfo,
                      quotationData,
                      discount,
                      discountType,
                    },
                  });
                }}
                disabled={products?.length === 0}
              >
                <FiFileText className="me-2" /> PDF Preview
              </button>

              {(quotationStatus === "Approved" || quotationStatus === "Requoted") && quotationStatus !== "Sent" ? (
                <button
                  className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-primary adminviewproducts-viewlead-d-flex adminviewproducts-viewlead-align-items-center me-2"
                  onClick={handleSendQuotation}
                  disabled={products?.length === 0 || sendingQuotation}
                >
                  {sendingQuotation ? (
                    <>
                      <span className="adminviewproducts-viewlead-spinner-border adminviewproducts-viewlead-spinner-border-sm adminviewproducts-viewlead-me-1" />
                      Preparing...
                    </>
                  ) : (
                    <>
                      <FiSend className="adminviewproducts-viewlead-me-1" />
                      Send Quotation
                    </>
                  )}
                </button>
              ) : quotationStatus === "Sent" ? (
                <span className="badge bg-success d-flex align-items-center px-3 py-2">
                  Sent 📩
                </span>
              ) : null}
            </div>
          </div>

          <div className="card shadow-lg border-0 rounded-3">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
                <div>
                  <h3 className="fw-bold text-primary mb-1">SBS Company</h3>
                  <p className="mb-0 small text-muted">
                    123 Business Street, Commercial Area
                  </p>
                  <p className="mb-0 small text-muted">
                    Mumbai, Maharashtra - 400001
                  </p>
                  <p className="mb-0 small text-muted">Phone: +91-9876543210</p>
                  <p className="mb-0 small text-muted">Email: contact@sbs.com</p>
                </div>
                <div className="text-end">
                  <h4 className="fw-bold text-decoration-underline text-dark">
                    QUOTATION
                  </h4>
                  <p className="mb-1">
                    <strong>No:</strong> {quotationNumber}
                  </p>
                  <p className="mb-0">
                    <strong>Date:</strong>{" "}
                    {new Date(data.quotationDate).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>

              <div className="border rounded p-3 bg-light mb-4">
                <h6 className="fw-bold text-secondary mb-2">Bill To:</h6>
                <p className="mb-1 fw-semibold">{leadInfo.lead_name}</p>
                <p className="mb-1 small">{leadInfo.email}</p>
                <p className="mb-1 small">{leadInfo.contact_number}</p>
                <p className="mb-0 small text-muted">
                  Lead Source: {leadInfo.lead_source}
                </p>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered align-middle text-center">
                  <thead className="table-primary">
                    <tr>
                      <th>Product</th>
                      <th>Product Image</th>
                      <th>HSN</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>GST %</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotationData.map((item, index) => {
                      const qty = parseInt(item.quantity) || 0;
                      const price = parseFloat(item.listprice) || 0;
                      const gstRate = parseFloat(item.gstrate) || 0;
                      const total = qty * price + (qty * price * gstRate) / 100;

                      return (
                        <tr key={index}>
                          <td>
                            {item.maincategory_name} <br />
                            {item.subcategory_name} <br />
                            {item.product_name} - {item.batch} <br />
                            Size: {item.size}
                          </td>
                          <td>
                            {item.product_image ? (
                              <a
                                href={`${baseURL}/Uploads/products/${item.product_image}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-inline-block"
                              >
                                <img
                                  src={`${baseURL}/Uploads/products/${item.product_image}`}
                                  alt={`${item.product_name || "Product"} image`}
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    border: "1px solid #dee2e6",
                                  }}
                                />
                              </a>
                            ) : (
                              <span className="text-muted">No Image</span>
                            )}
                          </td>
                          <td>{item.hsncode}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.listprice || 0)}</td>
                          <td>{item.gstrate || 0}%</td>
                          <td className="fw-bold">{formatCurrency(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="row justify-content-end mt-4">
                <div className="col-md-5">
                  <ul className="list-group shadow-sm">
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Subtotal:</span>
                      <strong>{formatCurrency(data.subtotal)}</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Discount ({discountType}):</span>
                      <strong>
                        {discountType === "percentage"
                          ? `${discount}%`
                          : formatCurrency(discount)}
                      </strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between">
                      <span>GST:</span>
                      <strong>{formatCurrency(data.totalGST)}</strong>
                    </li>
                    <li className="list-group-item d-flex justify-content-between bg-light">
                      <span className="fw-bold">Grand Total:</span>
                      <strong className="text-success fs-5">
                        {formatCurrency(data.grandTotal)}
                      </strong>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-4">
                <h6 className="fw-bold text-secondary">Quotation Body:</h6>
                <div className="small">
                  {leadInfo.quotation_body ? (
                    <div className="quotation-body-content bg-light p-3 rounded">
                      {leadInfo.quotation_body
                        .split("\n")
                        .filter((line) => line.trim())
                        .map((line, index, array) => {
                          const isSectionHeading = line.trim().endsWith(":");
                          return (
                            <div
                              key={index}
                              className={
                                isSectionHeading
                                  ? "fw-bold text-primary mb-2"
                                  : "mb-1"
                              }
                            >
                              {line.trim()}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="text-muted">No quotation details provided.</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <h6 className="fw-bold text-secondary">Terms & Conditions:</h6>
                <div className="small">
                  {leadInfo.terms_conditions ? (
                    <div className="terms-conditions-list">
                      {leadInfo.terms_conditions
                        .split("\n")
                        .filter((line) => line.trim())
                        .map((line, index) => (
                          <div key={index} className="d-flex mb-1">
                            <span
                              className="me-2 fw-medium text-primary"
                              style={{ minWidth: "20px" }}
                            >
                              {index + 1}.
                            </span>
                            <span>{line.trim()}</span>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className="text-muted">As per company policy.</p>
                  )}
                </div>
              </div>

              <div className="text-center mt-5 pt-3 border-top">
                <p className="mb-0 fw-semibold">Authorized Signature</p>
                <p className="mb-0 text-muted small">
                  Thank you for your business!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewQuotationPreview;